'use client';

import { useCallback, useState } from 'react';
import {
  fetchCatalogProviders,
  normalizeCatalogSearchFilters,
  type CatalogProviderLoaderResult,
  type CatalogProviderSummary,
  type NormalizedCatalogSearchFilters
} from '@/features/catalog';

export type UsePuppySearchOptions = {
  initialFilters: NormalizedCatalogSearchFilters;
  initialProviders: CatalogProviderSummary[];
  initialPagination: CatalogProviderLoaderResult['pagination'];
};

type SearchStatus = {
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
};

const DEFAULT_STATUS: SearchStatus = {
  isLoading: false,
  isLoadingMore: false,
  error: null
};

export type UsePuppySearchResult = {
  filters: NormalizedCatalogSearchFilters;
  providers: CatalogProviderSummary[];
  pagination: CatalogProviderLoaderResult['pagination'];
  status: SearchStatus;
  applyFilters: (
    partial: Partial<Omit<NormalizedCatalogSearchFilters, 'page'>>
  ) => Promise<NormalizedCatalogSearchFilters>;
  refresh: () => Promise<void>;
  loadNextPage: () => Promise<void>;
};

export function usePuppySearch({
  initialFilters,
  initialProviders,
  initialPagination
}: UsePuppySearchOptions): UsePuppySearchResult {
  // State is initialized from SSR props. When the route changes, the parent
  // component should pass a new `key` prop to force remount with fresh state.
  const [filters, setFilters] = useState(initialFilters);
  const [providers, setProviders] = useState(initialProviders);
  const [pagination, setPagination] = useState(initialPagination);
  const [status, setStatus] = useState<SearchStatus>(DEFAULT_STATUS);

  const runSearch = useCallback(
    async (
      nextFilters: NormalizedCatalogSearchFilters,
      { append }: { append?: boolean } = {}
    ) => {
      setStatus((prev) => ({
        ...prev,
        error: null,
        isLoading: append ? prev.isLoading : true,
        isLoadingMore: append ? true : false
      }));
      // Skip Next.js data cache for client-side searches to ensure fresh results
      const request = fetchCatalogProviders(nextFilters, { skipCache: true })
        .then((payload) => {
          setProviders((prev) => {
            if (!append) {
              return payload.items;
            }
            const seen = new Set(prev.map((provider) => provider.id));
            return [
              ...prev,
              ...payload.items.filter((provider) => !seen.has(provider.id))
            ];
          });
          setPagination({
            total: payload.total,
            totalItems: payload.totalItems,
            page: payload.page,
            limit: payload.limit,
            totalPages: payload.totalPages
          });
          setFilters(nextFilters);
        })
        .catch((error) => {
          setStatus((prev) => ({
            ...prev,
            error: error instanceof Error ? error : new Error('Search failed')
          }));
        })
        .finally(() => {
          setStatus((prev) => ({
            ...prev,
            isLoading: append ? prev.isLoading : false,
            isLoadingMore: append ? false : prev.isLoadingMore
          }));
        });
      await request;
    },
    []
  );

  const applyFilters = useCallback(
    async (partial: Partial<Omit<NormalizedCatalogSearchFilters, 'page'>>) => {
      const merged = normalizeCatalogSearchFilters({
        ...filters,
        ...partial,
        page: 1
      });
      await runSearch(merged, { append: false });
      return merged;
    },
    [filters, runSearch]
  );

  const refresh = useCallback(async () => {
    await runSearch(filters, { append: false });
  }, [filters, runSearch]);

  const loadNextPage = useCallback(async () => {
    if (pagination.page >= pagination.totalPages) {
      return;
    }
    const nextFilters = normalizeCatalogSearchFilters({
      ...filters,
      page: filters.page + 1
    });
    await runSearch(nextFilters, { append: true });
  }, [filters, pagination.page, pagination.totalPages, runSearch]);

  return {
    filters,
    providers,
    pagination,
    status,
    applyFilters,
    refresh,
    loadNextPage
  };
}
